import { useCallback, useMemo, useRef } from "react"
import { useDispatch } from "react-redux"
import { SchemaForm } from "universal-schema/lib"

import { getOnboardingAppSchema } from "../Actions/API/VisualEditorActions"
import { APIAppTemplateSchemaModel } from "../Models/API/AppTemplateModel"
import { FetchState, getValue, hasValue } from "../Models/Client/FetchState"
import { getAppSchema } from "../Models/Client/Schema"
import { Result } from "../Models/Utility/Result"
import { MainThunkDispatch } from "../Reducers"
import { useAppState } from "./useReduxState"
import { useAppTemplate } from "./useAppTemplate"
import { useAppInstance } from "./useAppInstance"

type AppSchema = {
    input: SchemaForm
    output: SchemaForm
}

export function useAppSchema(
    appID: string,
    marketplaceMode: boolean
): FetchState<AppSchema> {
    const instanceFetch = useAppInstance(appID)
    const templateFetch = useAppTemplate(appID)
    const prevAppSchema = useRef<AppSchema | undefined>(undefined)

    return useMemo(() => {
        if (!hasValue(instanceFetch)) {
            return instanceFetch
        }

        if (!hasValue(templateFetch)) {
            return templateFetch
        }

        const currentAppSchema = getAppSchema(
            instanceFetch.value,
            templateFetch.value,
            marketplaceMode
        )

        if (
            JSON.stringify(currentAppSchema) !==
            JSON.stringify(prevAppSchema.current)
        ) {
            prevAppSchema.current = currentAppSchema
        }
        return prevAppSchema.current
            ? Result.ok(prevAppSchema.current)
            : undefined
    }, [instanceFetch, marketplaceMode, templateFetch])
}

export function useOnboardingAppSchema(
    appID?: string
): [
    value: FetchState<APIAppTemplateSchemaModel>,
    fetch: (appParams: any) => void
] {
    const templateIdentifier = useAppState(
        state => state.api.interface.templateInstantiate?.templateIdentifier
    )
    const appTemplate = useAppState(state =>
        state.api.apps.apps.records.get(appID || "")
    )
    const templateName = templateIdentifier
        ? templateIdentifier
        : hasValue(appTemplate)
        ? getValue(appTemplate)?.templateName
        : undefined

    const appSchema = useAppState(state => state.api.visualEditor?.appSchema)
    const dispatch = useDispatch<MainThunkDispatch>()

    const fetch = useCallback(
        appParams => {
            if (templateName) {
                dispatch(
                    getOnboardingAppSchema({
                        templateName,
                        appParams
                    })
                )
            }
        },
        [dispatch, templateName]
    )

    return [appSchema, fetch]
}
